import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, tap, throwError } from 'rxjs';
import { ApiResponse } from '../../../../shared/dtos/api-response';
import { ApiError } from '../../../../shared/dtos/api-error';
import { DistributorMapper } from '../models/distributor';
import type { Distributor } from '../models/distributor';
import { DistributorsStore } from '../stores/distributors.store';
import {
  INTERNAL_MAC_DISTRIBUTOR_CHILDREN_ENDPOINT,
  INTERNAL_MAC_DISTRIBUTOR_ENDPOINT,
  INTERNAL_MAC_DISTRIBUTORS_ENDPOINT,
} from '../constants/endpoints';
import type { DistributorSummary, DistributorUpsertRequest } from '../dtos/distributor';

@Injectable({ providedIn: 'root' })
export class DistributorService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(DistributorsStore);

  /** Get a list of distributors from the backend */
  getDistributors(): Observable<Distributor[]> {
    this.store.setLoading();

    return this.http
      .get<ApiResponse<DistributorSummary[]>>(INTERNAL_MAC_DISTRIBUTORS_ENDPOINT)
      .pipe(
        map((resp) => DistributorMapper.fromDtoList(resp.data)),
        tap((records) => this.store.setRecords(records)),
        catchError((err: HttpErrorResponse) => {
          const body = err.error as ApiError | undefined;
          this.store.setError(body?.message ?? 'Failed to load distributors.');
          return throwError(() => body ?? { message: 'Failed to load distributors.' });
        }),
      );
  }

  /** Get children of a merchant */
  getChildren(merchantId: string): Observable<Distributor[]> {
    return this.http
      .get<
        ApiResponse<DistributorSummary[]>
      >(INTERNAL_MAC_DISTRIBUTOR_CHILDREN_ENDPOINT(merchantId))
      .pipe(map((resp) => DistributorMapper.fromDtoList(resp.data)));
  }

  /** Create a new distributor */
  createDistributor(body: DistributorUpsertRequest): Observable<Distributor> {
    return this.http
      .post<ApiResponse<DistributorSummary>>(INTERNAL_MAC_DISTRIBUTORS_ENDPOINT, body)
      .pipe(
        map((resp) => DistributorMapper.fromDto(resp.data)),
        tap((record) => this.store.addRecord(record)),
        catchError((err: HttpErrorResponse) => {
          const body = err.error as ApiError | undefined;
          return throwError(() => body ?? { message: 'Failed to create distributor.' });
        }),
      );
  }

  /** Update an existing distributor */
  updateDistributor(
    distributorId: number,
    body: DistributorUpsertRequest,
  ): Observable<Distributor> {
    return this.http
      .put<ApiResponse<DistributorSummary>>(INTERNAL_MAC_DISTRIBUTOR_ENDPOINT(distributorId), body)
      .pipe(
        map((resp) => DistributorMapper.fromDto(resp.data)),
        tap((record) => this.store.updateRecord(record)),
        catchError((err: HttpErrorResponse) => {
          const body = err.error as ApiError | undefined;
          return throwError(() => body ?? { message: 'Failed to update distributor.' });
        }),
      );
  }
}
